'use client'

import { DeviceStatus } from '@/api/deviceApi'
import { useRouteContext } from '@/app/layouts/TenantLayout'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import {
	SidebarGroup,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { isOrganizationLevelRoute } from '@/lib/routerUtils'
import { Link, useLocation, useRouter, useSearch } from '@tanstack/react-router'
import { Settings } from 'lucide-react'

import { PlayFilledIcon } from '@/components/icons/PlayFilledIcon'
import type { NavItem, NavSettingsOption } from '@/data/nav-data'
import { cn } from '@/lib/utils'
import { isPlatformUser, resolveDefaultTenantForOrg } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useMemo, useState } from 'react'

/** Nav item titles that render as a single link (no expandable children) in project nav */
const HIDE_CHILDREN_TITLES = new Set(['Device Management', 'Audit Logs', 'Sessions'])

type NavMainProps = {
	readonly items: NavItem[]
	/** When true, all items render as single links (e.g. settings, organization nav) */
	readonly hideAllChildren?: boolean
}

/** Strip /orgs/$orgId/projects/$tenantId or /orgs/$orgId prefix so path matches nav-data paths */
const normalizePath = (path: string) => {
	let p = path.replace(/\/+$/, '')
	// Project-level: /orgs/{orgId}/projects/{tenantId}/...
	if (/^\/orgs\/[^/]+\/projects\/[^/]+/.test(p)) {
		p = p.replace(/^\/orgs\/[^/]+\/projects\/[^/]+/, '') || '/'
	} else if (/^\/orgs\/[^/]+/.test(p)) {
		// Organization-level: /orgs/{orgId}/...
		p = p.replace(/^\/orgs\/[^/]+/, '') || '/'
	} else if (/^\/projects\/[^/]+/.test(p)) {
		// Legacy: /projects/{tenantId}/...
		p = p.replace(/^\/projects\/[^/]+/, '') || '/'
	}
	return p || '/'
}

const getBasePath = (path: string) => {
	const normalizedPath = normalizePath(path)
	const segments = normalizedPath.split('/').filter(Boolean)
	if (segments.length <= 1) {
		return normalizedPath
	}
	return `/${segments.slice(0, -1).join('/')}`
}

const shouldUseBasePathFallback = (path: string) => {
	const normalizedPath = normalizePath(path)
	const segments = normalizedPath.split('/').filter(Boolean)
	return segments.length > 2
}

const isPathActive = (path: string, currentPath: string) => {
	const isOrgLevelRoute = isOrganizationLevelRoute(path)

	if (isOrgLevelRoute) {
		// For organization-level routes, remove /orgs/$orgId prefix
		const pathWithoutPrefix = currentPath.replace(/^\/orgs\/[^/]+/, '')
		const navPathWithoutPrefix = path
		return pathWithoutPrefix === navPathWithoutPrefix || pathWithoutPrefix.startsWith(`${navPathWithoutPrefix}/`)
	}

	// For project-level routes, remove /orgs/$orgId/projects/$tenantId prefix
	const pathWithoutPrefix = currentPath.replace(/^\/orgs\/[^/]+\/projects\/[^/]+/, '')
	const navPathWithoutPrefix = path.replace(/^\/orgs\/[^/]+\/projects\/[^/]+/, '')

	// Exact match or starts with the path followed by a slash
	return pathWithoutPrefix === navPathWithoutPrefix || pathWithoutPrefix.startsWith(`${navPathWithoutPrefix}/`)
}

const isSectionPathActive = (path: string, currentPath: string) => {
	const normalizedCurrentPath = normalizePath(currentPath)
	const normalizedNavPath = normalizePath(path)

	if (isPathActive(normalizedNavPath, normalizedCurrentPath)) {
		return true
	}

	if (!shouldUseBasePathFallback(normalizedNavPath)) {
		return false
	}

	const baseNavPath = getBasePath(normalizedNavPath)
	return normalizedCurrentPath === baseNavPath || normalizedCurrentPath.startsWith(`${baseNavPath}/`)
}

// Settings tabs share a /settings prefix, so their section is 2 segments deep
// (/settings/users); 1 segment (/settings) would highlight every settings tab.
const getOrgSectionRoot = (normalizedPath: string) => {
	const segments = normalizedPath.split('/').filter(Boolean)
	const depth = segments[0] === 'settings' ? 2 : 1
	return `/${segments.slice(0, depth).join('/')}`
}

// relatedPaths[0] is the item's own URL; the rest are its settings options.
export const isSingleLinkNavItemActive = (relatedPaths: string[], currentPath: string): boolean => {
	if (relatedPaths.some(path => isSectionPathActive(path, currentPath))) {
		return true
	}

	// Org sections resolve to a default tab (/meters → /meters/devices), so match the section
	// root to keep sibling tabs highlighted. Org-only; project nav keeps stricter per-item matching.
	const actualUrl = relatedPaths[0]
	if (!actualUrl || !isOrganizationLevelRoute(actualUrl)) {
		return false
	}
	const sectionRoot = getOrgSectionRoot(normalizePath(actualUrl))
	const normalizedCurrent = normalizePath(currentPath)
	return normalizedCurrent === sectionRoot || normalizedCurrent.startsWith(`${sectionRoot}/`)
}

const resolveActualUrl = (routeInfo: { to: string; params: Record<string, string> | undefined }) => {
	let actualUrl = routeInfo.to
	if (routeInfo.params) {
		for (const [key, value] of Object.entries(routeInfo.params)) {
			actualUrl = actualUrl.replace(`$${key}`, value)
		}
	}
	return actualUrl
}

function NavMain({ items, hideAllChildren = false }: NavMainProps) {
	const { tenantId: routeTenantId, orgId: routeOrgId } = useRouteContext()
	const storeTenantId = useAuthStore(s => s.currentTenant)
	const storeOrgId = useAuthStore(s => s.currentOrganizationId)
	const user = useAuthStore(s => s.user)
	const orgId = routeOrgId || storeOrgId || undefined
	// On org-level URLs the tenantId param is absent. Resolve a fallback so project-scoped
	// nav links still build a valid /orgs/$orgId/projects/$tenantId/... URL:
	// 1) store tenant, only if it belongs to current org (prevents cross-org leak)
	// 2) first accessible tenant in current org
	const fallbackTenantId = useMemo(() => {
		if (!orgId || !user || !isPlatformUser(user)) {
			return undefined
		}
		if (storeTenantId && user.tenant_list?.some(t => t.id === storeTenantId && t.org_id === orgId)) {
			return storeTenantId
		}
		return resolveDefaultTenantForOrg(user, orgId) ?? undefined
	}, [storeTenantId, orgId, user])
	const tenantId = routeTenantId || fallbackTenantId
	const shouldHideChildren = (item: NavItem) => hideAllChildren || HIDE_CHILDREN_TITLES.has(item.title)

	// Helper function to generate proper route parameters
	// Handles both project-level routes (/orgs/$orgId/projects/$tenantId/*) and organization-level routes (/orgs/$orgId/*)
	const getRouteParams = (itemPath: string): { to: string; params: Record<string, string> | undefined } => {
		// Organization-level routes (inventory, meters, operations)
		// These only require orgId, not tenantId
		if (itemPath.includes('/inventory')) {
			if (!orgId) {
				return { to: itemPath, params: undefined }
			}
			const status = itemPath.split('/').pop() || DeviceStatus.All
			return {
				to: '/orgs/$orgId/inventory/$status',
				params: { orgId, status },
			}
		}
		if (itemPath.includes('/meters')) {
			if (!orgId) {
				return { to: itemPath, params: undefined }
			}
			let module = itemPath.split('/').pop() || 'devices'
			if (module === 'meters') {
				// If the path ends in 'meters', default to 'devices'
				module = 'devices'
			}
			return {
				to: '/orgs/$orgId/meters/$module',
				params: { orgId, module },
			}
		}
		if (itemPath.includes('/operations')) {
			if (!orgId) {
				return { to: itemPath, params: undefined }
			}
			let type = itemPath.split('/').pop() || 'all'
			if (type === 'operations') {
				// Parent "Operations" link: path is /operations, default to /operations/all
				type = 'all'
			}
			return {
				to: '/orgs/$orgId/operations/$type',
				params: { orgId, type },
			}
		}
		if (itemPath.includes('/billing')) {
			if (!orgId) {
				return { to: itemPath, params: undefined }
			}
			const type = itemPath.split('/').pop() || 'plan-details'
			return {
				to: '/orgs/$orgId/billing/$type',
				params: { orgId, type },
			}
		}
		if (itemPath.includes('/audits/audit-logs')) {
			if (!orgId) {
				return { to: itemPath, params: undefined }
			}
			if (routeTenantId) {
				const type = itemPath.split('/').pop() || 'all'
				return {
					to: '/orgs/$orgId/projects/$tenantId/settings/audits/audit-logs/$type',
					params: { orgId, tenantId: routeTenantId, type },
				}
			}
			return {
				to: '/orgs/$orgId/settings/audits/audit-logs',
				params: { orgId },
			}
		}
		if (itemPath.startsWith('/settings')) {
			if (!orgId) {
				return { to: itemPath, params: undefined }
			}
			// Stay in project settings only when the current route is actually project-scoped.
			// On org-level settings pages, `tenantId` may still have a fallback value from auth state.
			if (routeTenantId) {
				if (itemPath.includes('/settings/users/')) {
					const type = itemPath.split('/').pop() || 'all'
					return {
						to: '/orgs/$orgId/projects/$tenantId/settings/users/$type',
						params: { orgId, tenantId: routeTenantId, type },
					}
				}
				if (itemPath.includes('/settings/roles')) {
					const type = itemPath.split('/').pop()
					const resolvedType = type === 'roles' ? 'all' : type || 'all'
					return {
						to: '/orgs/$orgId/projects/$tenantId/settings/roles/$type',
						params: { orgId, tenantId: routeTenantId, type: resolvedType },
					}
				}
				if (itemPath.includes('/settings/api-keys')) {
					const type = itemPath.split('/').pop()
					const resolvedType = type === 'api-keys' ? 'all' : type || 'all'
					return {
						to: '/orgs/$orgId/projects/$tenantId/settings/api-keys/$type',
						params: { orgId, tenantId: routeTenantId, type: resolvedType },
					}
				}
				return {
					to: `/orgs/${orgId}/projects/${routeTenantId}${itemPath}`,
					params: undefined,
				}
			}
			if (itemPath.includes('/settings/api-keys')) {
				const type = itemPath.split('/').pop()
				const resolvedType = type === 'api-keys' ? 'all' : type || 'all'
				return {
					to: '/orgs/$orgId/settings/api-keys/$type',
					params: { orgId, type: resolvedType },
				}
			}
			return {
				to: `/orgs/${orgId}${itemPath}`,
				params: undefined,
			}
		}

		// Project-level routes require both orgId and tenantId
		if (!orgId || !tenantId) {
			return {
				to: itemPath,
				params: undefined,
			}
		}

		if (itemPath.includes('/device-management')) {
			const status = itemPath.split('/').pop() || DeviceStatus.Active
			return {
				to: '/orgs/$orgId/projects/$tenantId/device-management/$status',
				params: { orgId, tenantId, status },
			}
		}
		if (itemPath.includes('/data/dashboards')) {
			const type = itemPath.split('/').pop() || 'all'
			return {
				to: '/orgs/$orgId/projects/$tenantId/data/dashboards/$type',
				params: { orgId, tenantId, type },
			}
		}
		if (itemPath.includes('/actions/pushed')) {
			const status = itemPath.split('/').pop() || 'all'
			return {
				to: '/orgs/$orgId/projects/$tenantId/actions/pushed/$status',
				params: { orgId, tenantId, status },
			}
		}
		if (itemPath.includes('/sessions/rules')) {
			const type = itemPath.split('/').pop() || 'all'
			return {
				to: '/orgs/$orgId/projects/$tenantId/sessions/rules/$type',
				params: { orgId, tenantId, type },
			}
		}
		if (itemPath.includes('/sessions/dashboard')) {
			const parts = itemPath.split('/').filter(Boolean)
			const lastPart = parts[parts.length - 1] || 'all'
			const type = lastPart === 'dashboard' ? 'all' : lastPart
			return {
				to: '/orgs/$orgId/projects/$tenantId/sessions/dashboard/$type',
				params: { orgId, tenantId, type },
			}
		}
		if (itemPath.includes('/data/dbc-parsers')) {
			const type = itemPath.split('/').pop() || 'all'
			return {
				to: '/orgs/$orgId/projects/$tenantId/data/dbc-parsers/$type',
				params: { orgId, tenantId, type },
			}
		}
		if (itemPath.includes('/actions/assets')) {
			// Check if this is a specific tab (firmware/json/geofence) or just the parent path
			const pathParts = itemPath.split('/')
			const lastPart = pathParts[pathParts.length - 1]
			const type = ['files', 'json', 'geofence'].includes(lastPart) ? lastPart : 'files'
			return {
				to: '/orgs/$orgId/projects/$tenantId/actions/assets/$type',
				params: { orgId, tenantId, type },
			}
		}
		// Fallback for other project routes
		return {
			to: `/orgs/${orgId}/projects/${tenantId}${itemPath}`,
			params: undefined,
		}
	}

	return (
		<SidebarGroup>
			<SidebarMenu>
				{items.map(item => (
					<NavItemComponent
						key={item.title}
						item={item}
						getRouteParams={getRouteParams}
						hideChildren={shouldHideChildren}
					/>
				))}
			</SidebarMenu>
		</SidebarGroup>
	)
}

function NavItemComponent({
	item,
	getRouteParams,
	hideChildren,
}: {
	item: NavItem
	getRouteParams: (path: string) => { to: string; params: Record<string, string> | undefined }
	hideChildren: (item: NavItem) => boolean
}) {
	const currentSearch = useSearch({ strict: false })
	const router = useRouter()
	const location = useLocation()
	const currentPath = location.pathname
	const showSettingsInline = Boolean(item.hasSettings && item.settingsOptions?.length)
	const [settingItemOpen, setSettingItemOpen] = useState(false)

	const settingsOptionList = item.settingsOptions ?? []

	const navigateToSettingsOption = (option: NavSettingsOption) => {
		const routeInfo = getRouteParams(option.path)
		if (routeInfo.params) {
			router.navigate({ to: routeInfo.to, params: routeInfo.params })
		} else {
			router.navigate({ to: routeInfo.to })
		}
	}

	const renderSettingsDropdown = () => {
		if (!showSettingsInline) {
			return null
		}

		// A lone settings option navigates directly — no dropdown to open. The tooltip
		// names the destination so the gear isn't an unlabelled mystery icon.
		if (settingsOptionList.length === 1) {
			const option = settingsOptionList[0]
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type='button'
							aria-label={option.label}
							className='ml-1 opacity-0 cursor-pointer p-1 rounded-sm transition-opacity duration-200 group-hover/sidebar-btn:opacity-100 group-data-[collapsible=icon]/sidebar:hidden'
							onClick={e => {
								e.preventDefault()
								e.stopPropagation()
								navigateToSettingsOption(option)
							}}>
							<Settings className='size-(--icon-standalone)' />
						</button>
					</TooltipTrigger>
					<TooltipContent side='right' sideOffset={16} hideArrow>
						{option.label}
					</TooltipContent>
				</Tooltip>
			)
		}

		return (
			<DropdownMenu open={settingItemOpen} onOpenChange={setSettingItemOpen}>
				<DropdownMenuTrigger asChild>
					<div className='ml-1 opacity-0 cursor-pointer p-1 rounded-sm transition-opacity duration-200 group-hover/sidebar-btn:opacity-100 group-data-[collapsible=icon]/sidebar:hidden'>
						<Settings className='size-(--icon-standalone)' />
					</div>
				</DropdownMenuTrigger>
				<DropdownMenuContent side='left' align='start' className='space-y-2'>
					{item.settingsOptions?.map(option => {
						const routeInfo = getRouteParams(option.path)
						const actualUrl = resolveActualUrl(routeInfo)
						const isActive = isSectionPathActive(actualUrl, currentPath)

						return (
							<DropdownMenuItem
								key={option.label}
								className={cn(
									'gap-2 cursor-pointer text-body h-control! min-h-control!',
									isActive
										? 'bg-sidebar-accent text-header-tab-active-text font-medium hover:bg-sidebar-accent-hover focus:bg-sidebar-accent-hover'
										: 'text-header-tab-inactive-text hover:bg-sidebar-accent focus:bg-sidebar-accent hover:text-header-tab-active-text focus:text-header-tab-active-text',
								)}
								onClick={e => {
									e.preventDefault()
									e.stopPropagation() // Prevent collapsing
									if (routeInfo.params) {
										router.navigate({ to: routeInfo.to, params: routeInfo.params })
									} else {
										router.navigate({ to: routeInfo.to })
									}
									// close the dropdown
									setSettingItemOpen(false)
								}}>
								{option.icon && (
									<option.icon
										className={cn(
											'size-(--icon-standalone) shrink-0',
											isActive ? 'text-header-tab-active-text' : 'text-header-tab-inactive-text',
										)}
									/>
								)}
								{option.label}
							</DropdownMenuItem>
						)
					})}
				</DropdownMenuContent>
			</DropdownMenu>
		)
	}

	if (!item.items?.length || hideChildren(item)) {
		const routeInfo = getRouteParams(item.path)
		const actualUrl = resolveActualUrl(routeInfo)

		const relatedPaths = [
			actualUrl,
			...(item.settingsOptions?.map(option => resolveActualUrl(getRouteParams(option.path))) ?? []),
		]

		const isActive = isSingleLinkNavItemActive(relatedPaths, currentPath)
		return (
			<SidebarMenuItem>
				<div className='flex items-center group/sidebar-btn ml-1'>
					<Link to={routeInfo.to} params={routeInfo.params} search={currentSearch} preload='intent' className='flex-1'>
						<SidebarMenuButton asChild isActive={isActive} className='flex-1'>
							<div className='flex items-center gap-1.5 flex-1'>
								{item.icon && (
									<item.icon
										className={cn(
											'size-(--icon-standalone) shrink-0',
											isActive
												? 'text-sidebar-active-text'
												: 'text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text',
										)}
									/>
								)}
								<span
									className={cn(
										'flex-1 text-body',
										isActive
											? 'text-sidebar-active-text'
											: 'text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text',
									)}>
									{item.title}
								</span>
							</div>
						</SidebarMenuButton>
					</Link>
					{renderSettingsDropdown()}
				</div>
			</SidebarMenuItem>
		)
	}

	return (
		<Collapsible asChild defaultOpen={true} className='group/collapsible'>
			<SidebarMenuItem>
				<div className='flex items-center group/sidebar-btn mb-1'>
					<CollapsibleTrigger asChild>
						<SidebarMenuButton className='flex'>
							<div className='flex items-baseline gap-2 justify-start flex-1'>
								<span className='align-middle text-body text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text'>
									{item.title}
								</span>
								<PlayFilledIcon
									className={cn(
										'size-2.5 shrink-0 align-middle transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text',
									)}
								/>
							</div>
						</SidebarMenuButton>
					</CollapsibleTrigger>
					<div className='ml-1 w-6 h-6 flex items-center justify-center'>{renderSettingsDropdown()}</div>
				</div>

				<CollapsibleContent>
					<SidebarMenuSub>
						{item.items.map(subItem => {
							const routeInfo = getRouteParams(subItem.path)
							const actualUrl = resolveActualUrl(routeInfo)

							const isActive = isSectionPathActive(actualUrl, currentPath)
							return (
								<SidebarMenuSubItem key={subItem.title}>
									<SidebarMenuSubButton asChild isActive={isActive}>
										<Link
											to={routeInfo.to}
											params={routeInfo.params}
											search={currentSearch}
											preload='intent'
											className='flex items-center gap-1.5 px-3 ml-1'>
											{subItem.icon && (
												<subItem.icon
													className={cn(
														'size-(--icon-standalone) shrink-0',
														isActive
															? 'text-sidebar-active-text!'
															: 'text-sidebar-inactive-text! group-hover/menu-sub-item:text-sidebar-active-text!',
													)}
												/>
											)}
											<span
												className={cn(
													'text-body',
													isActive
														? 'text-sidebar-active-text'
														: 'text-sidebar-inactive-text group-hover/menu-sub-item:text-sidebar-active-text',
												)}>
												{subItem.title}
											</span>
										</Link>
									</SidebarMenuSubButton>
								</SidebarMenuSubItem>
							)
						})}
					</SidebarMenuSub>
				</CollapsibleContent>
			</SidebarMenuItem>
		</Collapsible>
	)
}

export { NavMain }
